import { animate, buildTransform, inView, stagger } from 'motion'

/**
 * `data-anim` animates the block element itself.
 * `data-anim-children` animates either direct children or root-scoped selector matches.
 */

const animationSelector = '[data-anim], [data-anim-children]'
const initializedAttr = 'data-anim-initialized'
const anchorAttr = 'data-anim-anchor'
const childrenAnimationAttr = 'data-anim-children'
const childrenInitializedAttr = 'data-anim-children-initialized'
const childrenDelayAttr = 'data-anim-children-delay'
const childrenDurationAttr = 'data-anim-children-duration'
const childrenEasingAttr = 'data-anim-children-easing'
const childrenOffsetAttr = 'data-anim-children-offset'
const childrenOnceAttr = 'data-anim-children-once'
const childrenSelectorAttr = 'data-anim-children-selector'
const childrenStaggerDelayAttr = 'data-anim-children-stagger-delay'

const defaultDuration = 400
const defaultDistance = 24
const defaultSlideDistance = 100
const defaultZoomScale = 0.85
const defaultZoomOutScale = 1.15
const defaultFlipPerspective = 1200

const directionalOffsetMap = {
  down: [0, -defaultDistance],
  'down-left': [defaultDistance, -defaultDistance],
  'down-right': [-defaultDistance, -defaultDistance],
  left: [defaultDistance, 0],
  right: [-defaultDistance, 0],
  up: [0, defaultDistance],
  'up-left': [defaultDistance, defaultDistance],
  'up-right': [-defaultDistance, defaultDistance],
}

const slideDirectionalOffsetMap = {
  down: [0, -defaultSlideDistance],
  'down-left': [defaultSlideDistance, -defaultSlideDistance],
  'down-right': [-defaultSlideDistance, -defaultSlideDistance],
  left: [defaultSlideDistance, 0],
  right: [-defaultSlideDistance, 0],
  up: [0, defaultSlideDistance],
  'up-left': [defaultSlideDistance, defaultSlideDistance],
  'up-right': [-defaultSlideDistance, defaultSlideDistance],
}

const flipTransformMap = {
  down: { rotateX: 90, transformPerspective: defaultFlipPerspective },
  left: { rotateY: -90, transformPerspective: defaultFlipPerspective },
  right: { rotateY: 90, transformPerspective: defaultFlipPerspective },
  up: { rotateX: -90, transformPerspective: defaultFlipPerspective },
}

const anchorAmountMap = {
  bottom: 'all',
  center: 0.5,
  top: 'some',
}

const anchorMarginMap = {
  bottom: { baseMargin: 0, unit: 'px' },
  center: { baseMargin: 50, unit: '%' },
  top: { baseMargin: 90, unit: '%' },
}

const easingMap = {
  'ease-in-back': 'cubic-bezier(.6,-.28,.735,.045)',
  'ease-in-cubic': 'cubic-bezier(.55,.055,.675,.19)',
  'ease-in-out-back': 'cubic-bezier(.68,-.55,.265,1.55)',
  'ease-in-out-cubic': 'cubic-bezier(.645,.045,.355,1)',
  'ease-in-out-quad': 'cubic-bezier(.455,.03,.515,.955)',
  'ease-in-out-quart': 'cubic-bezier(.77,0,.175,1)',
  'ease-in-out-sine': 'cubic-bezier(.445,.05,.55,.95)',
  'ease-in-quad': 'cubic-bezier(.55,.085,.68,.53)',
  'ease-in-quart': 'cubic-bezier(.895,.03,.685,.22)',
  'ease-in-sine': 'cubic-bezier(.47,0,.745,.715)',
  'ease-out-back': 'cubic-bezier(.175,.885,.32,1.275)',
  'ease-out-cubic': 'cubic-bezier(.215,.61,.355,1)',
  'ease-out-quad': 'cubic-bezier(.25,.46,.45,.94)',
  'ease-out-quart': 'cubic-bezier(.165,.84,.44,1)',
  'ease-out-sine': 'cubic-bezier(.39,.575,.565,1)',
}

const animationRegistrations = new Map()

let mutationObserver = null

/**
 * Parses a numeric data attribute while preserving a caller-provided fallback.
 *
 * @param {string|null} value
 * @param {number} fallback
 * @returns {number}
 */
function parseNumber(value, fallback) {
  if (!value) {
    return fallback
  }

  const parsedValue = Number.parseInt(value, 10)
  return Number.isNaN(parsedValue) ? fallback : parsedValue
}

/**
 * Rounds animation numbers to a short stable value for inline CSS.
 *
 * @param {number} value
 * @returns {number}
 */
function formatNumber(value) {
  return Number.parseFloat(value.toFixed(4))
}

/**
 * Formats a numeric pixel value for generated transform strings.
 *
 * @param {number} value
 * @returns {string}
 */
function formatPx(value) {
  return `${formatNumber(value)}px`
}

/**
 * Maps legacy AOS easing names to Motion-compatible easing values.
 *
 * @param {string|null} value
 * @returns {string}
 */
function getEasing(value) {
  if (!value) {
    return 'ease'
  }

  return easingMap[value] || value
}

/**
 * Creates a partial transform object for directional reveal animations.
 *
 * @param {number} x
 * @param {number} y
 * @returns {Record<string, number>}
 */
function createTranslateTransform(x, y) {
  return {
    ...(x ? { x } : {}),
    ...(y ? { y } : {}),
  }
}

/**
 * Creates a transform object for zoom animations that also move.
 *
 * @param {number} x
 * @param {number} y
 * @param {number} scale
 * @returns {Record<string, number>}
 */
function createTranslateAndScaleTransform(x, y, scale) {
  return {
    ...createTranslateTransform(x, y),
    scale,
  }
}

const presetBuilders = {
  fade(direction) {
    if (!direction) {
      return createPreset()
    }

    const offset = directionalOffsetMap[direction]
    return offset ? createPreset(createTranslateTransform(offset[0], offset[1])) : null
  },
  flip(direction) {
    return direction && flipTransformMap[direction] ? createPreset(flipTransformMap[direction]) : null
  },
  slide(direction) {
    const offset = slideDirectionalOffsetMap[direction]
    return offset ? createPreset(createTranslateTransform(offset[0], offset[1])) : null
  },
  'zoom-in'(direction) {
    if (!direction) {
      return createPreset({ scale: defaultZoomScale })
    }

    const offset = directionalOffsetMap[direction]
    return offset ? createPreset(createTranslateAndScaleTransform(offset[0], offset[1], defaultZoomScale)) : null
  },
  'zoom-out'(direction) {
    if (!direction) {
      return createPreset({ scale: defaultZoomOutScale })
    }

    const offset = directionalOffsetMap[direction]
    return offset ? createPreset(createTranslateAndScaleTransform(offset[0], offset[1], defaultZoomOutScale)) : null
  },
}

/**
 * Returns the natural visible value for a transform property.
 *
 * @param {string} key
 * @param {number} value
 * @returns {number}
 */
function getDefaultTransformValue(key, value) {
  if (key === 'scale') {
    return 1
  }

  if (key === 'transformPerspective') {
    return value
  }

  return 0
}

/**
 * Builds the visible target transform for an initial hidden transform.
 *
 * @param {Record<string, number>} transform
 * @returns {Record<string, number>}
 */
function getTargetTransform(transform) {
  return Object.keys(transform).reduce((targetTransform, key) => {
    targetTransform[key] = getDefaultTransformValue(key, transform[key])
    return targetTransform
  }, {})
}

/**
 * Creates the initial and target style states for one animation preset.
 *
 * Presets are stored as numeric style objects so Motion can animate them
 * directly, while the fallback inline styles can still be generated manually.
 *
 * @param {Record<string, number>} transform
 * @returns {{initial: Record<string, number>, target: Record<string, number>}}
 */
function createPreset(transform = {}) {
  return {
    initial: {
      opacity: 0,
      ...transform,
    },
    target: {
      opacity: 1,
      ...getTargetTransform(transform),
    },
  }
}

/**
 * Converts a stored animation name into initial and target styles.
 *
 * @param {string} animation
 * @returns {{initial: Record<string, number>, target: Record<string, number>}}
 */
function getAnimationPreset(animation) {
  const animationName = animation || 'fade'
  const builderKey = Object.keys(presetBuilders).find(
    (key) => animationName === key || animationName.startsWith(`${key}-`),
  )
  const direction = builderKey && animationName !== builderKey ? animationName.slice(builderKey.length + 1) : ''
  const preset = builderKey ? presetBuilders[builderKey](direction) : null

  return preset || presetBuilders.fade('')
}

/**
 * Converts Motion-style numeric transform properties into a CSS transform.
 *
 * @param {Record<string, number>} styles
 * @returns {string}
 */
function formatTransform(styles) {
  const transform = {}
  const transformStyles = Object.entries(styles).reduce((accumulator, [key, value]) => {
    if (key === 'opacity') {
      return accumulator
    }

    accumulator[key] = key === 'transformPerspective' ? formatPx(value) : value
    return accumulator
  }, {})

  const value = buildTransform(transformStyles, transform)
  return value === 'none' ? '' : value
}

/**
 * Applies the generated opacity and transform fallback styles to one target.
 *
 * @param {*} element
 * @param {Record<string, number>} styles
 */
function applyStyles(element, styles) {
  element.style.opacity = `${formatNumber(styles.opacity)}`
  element.style.transform = formatTransform(styles)
}

/**
 * Applies the same fallback styles to a whole animation target set.
 *
 * @param {Array<*>} targets
 * @param {Record<string, number>} styles
 */
function applyStylesToTargets(targets, styles) {
  targets.forEach((target) => {
    applyStyles(target, styles)
  })
}

/**
 * Builds explicit from/to keyframes for Motion.
 *
 * Using explicit keyframes keeps replay reliable because each entry animation
 * starts from the preset initial state, regardless of the element's current
 * inline style after a previous viewport transition.
 *
 * @param {Record<string, number>} initialStyles
 * @param {Record<string, number>} targetStyles
 * @returns {Record<string, number[]>}
 */
function getKeyframeStyles(initialStyles, targetStyles) {
  return Object.keys({ ...initialStyles, ...targetStyles }).reduce((keyframes, key) => {
    const initialValue = initialStyles[key] ?? getDefaultTransformValue(key, targetStyles[key])
    const targetValue = targetStyles[key] ?? getDefaultTransformValue(key, initialValue)

    keyframes[key] = [initialValue, targetValue]
    return keyframes
  }, {})
}

/**
 * Stops either a Motion animation or a compatible animation-like object.
 *
 * @param {{cancel?: Function, stop?: Function}|null} animation
 */
function stopAnimation(animation) {
  if (!animation) {
    return
  }

  if (typeof animation.cancel === 'function') {
    animation.cancel()
    return
  }

  if (typeof animation.stop === 'function') {
    animation.stop()
  }
}

/**
 * Maps AOS-style anchor placement into Motion `inView` pieces.
 *
 * @param {string|null} value
 * @returns {{amount: string|number, margin: {baseMargin: number, unit: string}}}
 */
function getAnchorPlacement(value) {
  const [targetEdge = 'top', viewportEdge = 'bottom'] = (value || 'top-bottom').split('-')

  return {
    amount: anchorAmountMap[targetEdge] || 'some',
    margin: anchorMarginMap[viewportEdge] || anchorMarginMap.center,
  }
}

/**
 * Returns the viewport height when available.
 *
 * @returns {number}
 */
function getViewportHeight() {
  return typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 0
}

/**
 * Converts the viewport edge part of an anchor placement into a bottom margin.
 *
 * @param {{baseMargin: number, unit: string}} margin
 * @param {number} offset
 * @returns {string|null}
 */
function getBottomMargin(margin, offset) {
  if (margin.unit === 'px') {
    return offset ? `-${offset}px` : null
  }

  const offsetPercent = offset && getViewportHeight() ? (offset / getViewportHeight()) * 100 : 0

  return `-${formatNumber(Math.min(99, margin.baseMargin + offsetPercent))}%`
}

/**
 * Builds the Motion `inView` options for an animation group.
 *
 * `data-anim-anchor` keeps the old AOS placement vocabulary but maps it to a
 * simpler threshold/margin model.
 *
 * @param {{anchor?: string|null, offset?: number}} options
 * @returns {import('motion').InViewOptions}
 */
function getInViewOptions(options = {}) {
  const offset = Math.max(0, options.offset || 0)
  const placement = getAnchorPlacement(options.anchor)
  const bottomMargin = getBottomMargin(placement.margin, offset)
  const inViewOptions = {
    amount: placement.amount,
  }

  if (bottomMargin) {
    inViewOptions.margin = /** @type {import('motion').InViewOptions['margin']} */ (`0px 0px ${bottomMargin} 0px`)
  }

  return inViewOptions
}

/**
 * Resolves child animation targets from the animation root.
 *
 * An empty selector means direct element children. A selector is scoped to the
 * current root; invalid or unmatched selectors intentionally produce no work.
 *
 * @param {*} rootElement
 * @param {string|null} selector
 * @returns {Array<*>}
 */
function getScopedAnimationTargets(rootElement, selector = null) {
  if (!selector) {
    return Array.from(rootElement.children).filter((child) => child.nodeType === 1)
  }

  try {
    return Array.from(rootElement.querySelectorAll(selector))
  } catch (_error) {
    return []
  }
}

/**
 * Builds one normalized descriptor from root attributes.
 *
 * @param {*} rootElement
 * @param {'self'|'children'} mode
 * @returns {{
 *     rootElement: *,
 *     targets: Array<*>,
 *     preset: {initial: Record<string, number>, target: Record<string, number>},
 *     delay: number,
 *     duration: number,
 *     easing: string,
 *     once: boolean,
 *     mirror: boolean,
 *     offset: number,
 *     anchor: string|null,
 *     staggerDelay: number,
 *     initializedAttr: string
 * }|null}
 */
function getAnimationDescriptor(rootElement, mode) {
  if (mode === 'self') {
    if (rootElement.hasAttribute(initializedAttr)) {
      return null
    }

    const animation = rootElement.getAttribute('data-anim')
    if (!animation) {
      return null
    }

    return {
      anchor: rootElement.getAttribute(anchorAttr) || null,
      delay: parseNumber(rootElement.getAttribute('data-anim-delay'), 0),
      duration: parseNumber(rootElement.getAttribute('data-anim-duration'), defaultDuration),
      easing: getEasing(rootElement.getAttribute('data-anim-easing')),
      initializedAttr,
      mirror: rootElement.getAttribute('data-anim-mirror') === 'true',
      offset: parseNumber(rootElement.getAttribute('data-anim-offset'), 0),
      once: rootElement.getAttribute('data-anim-once') === 'true',
      preset: getAnimationPreset(animation),
      rootElement,
      staggerDelay: 0,
      targets: [rootElement],
    }
  }

  if (rootElement.hasAttribute(childrenInitializedAttr)) {
    return null
  }

  const animation = rootElement.getAttribute(childrenAnimationAttr)
  if (!animation) {
    return null
  }

  return {
    anchor: null,
    delay: parseNumber(rootElement.getAttribute(childrenDelayAttr), 0),
    duration: parseNumber(rootElement.getAttribute(childrenDurationAttr), defaultDuration),
    easing: getEasing(rootElement.getAttribute(childrenEasingAttr)),
    initializedAttr: childrenInitializedAttr,
    mirror: false,
    offset: parseNumber(rootElement.getAttribute(childrenOffsetAttr), 0),
    once: rootElement.getAttribute(childrenOnceAttr) === 'true',
    preset: getAnimationPreset(animation),
    rootElement,
    staggerDelay: parseNumber(rootElement.getAttribute(childrenStaggerDelayAttr), 80),
    targets: getScopedAnimationTargets(rootElement, rootElement.getAttribute(childrenSelectorAttr) || null),
  }
}

/**
 * Converts stored millisecond timing into Motion animation options.
 *
 * Child animations use Motion's `stagger()` so a single grouped animation can
 * handle both selector targets and direct child targets.
 *
 * @param {{delay: number, duration: number, easing: string, staggerDelay: number}} options
 * @param {number} targetCount
 * @returns {object}
 */
function getMotionOptions(options, targetCount) {
  const staggerDelay = Math.max(0, options.staggerDelay || 0)
  const delay =
    staggerDelay && targetCount > 1
      ? stagger(staggerDelay / 1000, { startDelay: options.delay / 1000 })
      : options.delay / 1000

  return {
    delay,
    duration: options.duration / 1000,
    ease: options.easing,
  }
}

/**
 * Plays one Motion animation between two preset states.
 *
 * Inline styles are set before and after Motion runs so elements have a stable
 * fail-safe state if a viewport transition is missed or an animation is stopped.
 *
 * @param {Array<*>} targets
 * @param {Record<string, number>} fromStyles
 * @param {Record<string, number>} toStyles
 * @param {{delay: number, duration: number, easing: string, staggerDelay: number}} options
 * @returns {*}
 */
function playAnimation(targets, fromStyles, toStyles, options) {
  applyStylesToTargets(targets, fromStyles)

  const animation = animate(
    /** @type {any} */ (targets),
    /** @type {any} */ (getKeyframeStyles(fromStyles, toStyles)),
    /** @type {any} */ (getMotionOptions(options, targets.length)),
  )

  animation.finished
    ?.then(() => {
      applyStylesToTargets(targets, toStyles)
    })
    .catch(() => {})

  return animation
}

/**
 * Stores one cleanup/animation registration for a root element.
 *
 * @param {*} element
 * @param {{animation: *|null, cleanup: Function|null}} registration
 */
function addRegistration(element, registration) {
  const registrations = animationRegistrations.get(element) || []
  registrations.push(registration)
  animationRegistrations.set(element, registrations)
}

/**
 * Starts one Motion animation and clears the stored reference when it settles.
 *
 * @param {{animation: *|null}} registration
 * @param {Array<*>} targets
 * @param {Record<string, number>} fromStyles
 * @param {Record<string, number>} toStyles
 * @param {{delay: number, duration: number, easing: string, staggerDelay: number}} options
 * @returns {*}
 */
function startRegisteredAnimation(registration, targets, fromStyles, toStyles, options) {
  const animation = playAnimation(targets, fromStyles, toStyles, options)
  registration.animation = animation
  animation.finished
    ?.then(() => {
      if (registration.animation === animation) {
        registration.animation = null
      }
    })
    .catch(() => {})

  return animation
}

/**
 * Registers one viewport-triggered animation group.
 *
 * The same path handles block self-animations and child animations.
 * Non-mirror animations become visible on leave;
 * Mirror animations animate back to the hidden state, matching AOS mirror behavior.
 *
 * @param {*} rootElement
 * @param {Array<*>} targets
 * @param {{initial: Record<string, number>, target: Record<string, number>}} preset
 * @param {{
 *     anchor: string|null,
 *     delay: number,
 *     duration: number,
 *     easing: string,
 *     mirror: boolean,
 *     offset: number,
 *     once: boolean,
 *     staggerDelay: number
 * }} options
 * @returns {boolean}
 */
function registerAnimationGroup(rootElement, targets, preset, options) {
  if (!targets.length) {
    return false
  }

  const registration = {
    animation: null,
    cleanup: null,
  }

  applyStylesToTargets(targets, preset.initial)

  registration.cleanup = inView(
    rootElement,
    () => {
      stopAnimation(registration.animation)
      startRegisteredAnimation(registration, targets, preset.initial, preset.target, options)

      if (options.once) {
        return undefined
      }

      return () => {
        stopAnimation(registration.animation)

        if (options.mirror) {
          startRegisteredAnimation(registration, targets, preset.target, preset.initial, options)
          return
        }

        registration.animation = null
        applyStylesToTargets(targets, preset.target)
      }
    },
    getInViewOptions(options),
  )

  addRegistration(rootElement, registration)
  return true
}

/**
 * Tears down all registrations owned by one root element.
 *
 * @param {*} element
 */
function cleanupAnimationElement(element) {
  const registrations = animationRegistrations.get(element)
  if (!registrations) {
    return
  }

  registrations.forEach((registration) => {
    registration.cleanup?.()
    stopAnimation(registration.animation)
  })
  animationRegistrations.delete(element)
  element.removeAttribute(initializedAttr)
  element.removeAttribute(childrenInitializedAttr)
}

/**
 * Cleans up animation registrations inside a removed DOM subtree.
 *
 * @param {*} node
 */
function cleanupRemovedAnimations(node) {
  if (node.nodeType !== 1) {
    return
  }

  const element = node
  cleanupAnimationElement(element)

  element.querySelectorAll(animationSelector).forEach((childElement) => {
    cleanupAnimationElement(childElement)
  })
}

function initializeAnimationElement(element) {
  const modes = /** @type {Array<'self'|'children'>} */ (['self', 'children'])

  modes.forEach((mode) => {
    const descriptor = getAnimationDescriptor(element, mode)
    if (!descriptor) {
      return
    }

    const { initializedAttr: descriptorInitializedAttr, preset, rootElement, targets, ...options } = descriptor
    if (registerAnimationGroup(rootElement, targets, preset, options)) {
      element.setAttribute(descriptorInitializedAttr, '1')
    }
  })
}

/**
 * Initializes all animation elements currently present in a container.
 *
 * @param {*} container
 */
function initializeExistingElements(container) {
  if (container.matches?.(animationSelector)) {
    initializeAnimationElement(container)
  }

  container.querySelectorAll(animationSelector).forEach((element) => {
    initializeAnimationElement(element)
  })
}

/**
 * Watches for dynamically added or removed animation roots.
 *
 * Only child-list changes are observed because animation attributes are rendered
 * by the backend and are not expected to mutate in place on the published site.
 *
 * @param {*} container
 */
function observeNewElements(container) {
  if (mutationObserver) {
    return
  }

  mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        cleanupRemovedAnimations(node)
      })

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) {
          return
        }

        const element = node
        if (element.matches(animationSelector)) {
          initializeAnimationElement(element)
        }

        element.querySelectorAll(animationSelector).forEach((childElement) => {
          initializeAnimationElement(childElement)
        })
      })
    })
  })

  mutationObserver.observe(container, {
    attributes: false,
    childList: true,
    subtree: true,
  })
}

/**
 * Initializes all published-site animations inside a container.
 *
 * @param {*|null} container
 */
function setupAnimations(container) {
  if (!container) {
    return
  }

  if (!container.matches?.(animationSelector) && !container.querySelector(animationSelector)) {
    return
  }

  initializeExistingElements(container)
  observeNewElements(container)
}

/**
 * Stops all active animations and disconnects DOM observers.
 *
 * This is primarily useful for tests and any future client-side page swaps.
 */
function destroyAnimations() {
  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }

  Array.from(animationRegistrations.keys()).forEach((element) => {
    cleanupAnimationElement(element)
  })
}

export { setupAnimations, destroyAnimations }
